/**
 * Parsing Oura API v2 responses.
 *
 * Shapes follow the existing, proven pull on the Mac mini (pull_oura.py),
 * whose ledger schema this deliberately mirrors so historical data imports
 * without translation.
 *
 * Note on Oura's API: HRV, resting heart rate and temperature have NO
 * dedicated endpoints. HRV and RHR live inside `sleep` documents; temperature
 * deviation lives inside `daily_readiness`. The sleep-record selection below
 * matters for the same reason it matters in the Python version: a short
 * afternoon nap must not shadow the night's HRV and resting heart rate.
 */
import type { Day, Reading } from './schema';

export interface OuraBundle {
  daily_sleep?: { data?: OuraDailySleep[] };
  daily_readiness?: { data?: OuraDailyReadiness[] };
  daily_activity?: { data?: OuraDailyActivity[] };
  sleep?: { data?: OuraSleep[] };
  daily_spo2?: { data?: OuraSpo2[] };
  [key: string]: unknown;
}

interface OuraDailySleep { day?: string; score?: number }
interface OuraDailyReadiness { day?: string; score?: number; temperature_deviation?: number }
interface OuraDailyActivity { day?: string; score?: number; steps?: number; active_calories?: number }
interface OuraSpo2 { day?: string; spo2_percentage?: { average?: number } }
interface OuraSleep {
  day?: string;
  type?: string;
  total_sleep_duration?: number;
  rem_sleep_duration?: number;
  deep_sleep_duration?: number;
  average_hrv?: number;
  lowest_heart_rate?: number;
  average_heart_rate?: number;
}

/**
 * The night's main sleep record for a day.
 * Prefers a `long_sleep` typed record, then the longest by total duration --
 * the same rule the existing Python pull uses.
 */
export function mainSleepRecord(records: OuraSleep[]): OuraSleep | null {
  if (!records.length) return null;
  const long = records.filter((r) => r.type === 'long_sleep');
  const pool = long.length ? long : records;
  return pool.reduce((best, r) =>
    (r.total_sleep_duration ?? 0) > (best.total_sleep_duration ?? 0) ? r : best, pool[0]!);
}

export interface ParsedOura {
  days: Map<Day, Record<string, Reading>>;
  warnings: string[];
}

/** Turn an Oura bundle into per-day readings. */
export function parseOuraBundle(bundle: unknown, fetchedAt: string = new Date().toISOString()): ParsedOura {
  const out: ParsedOura = { days: new Map(), warnings: [] };
  if (!bundle || typeof bundle !== 'object') {
    out.warnings.push('Oura payload was not an object.');
    return out;
  }
  const b = bundle as OuraBundle;
  const set = (day: string | undefined, field: string, value: unknown) => {
    if (!day || typeof value !== 'number' || !Number.isFinite(value)) return;
    const bucket = out.days.get(day) ?? {};
    bucket[field] = { value, source: 'oura', at: fetchedAt };
    out.days.set(day, bucket);
  };

  for (const d of b.daily_sleep?.data ?? []) set(d.day, 'sleepScore', d.score);
  for (const d of b.daily_readiness?.data ?? []) {
    set(d.day, 'readinessScore', d.score);
    set(d.day, 'tempDeviationC', d.temperature_deviation);
  }
  for (const d of b.daily_activity?.data ?? []) {
    set(d.day, 'activityScore', d.score);
    set(d.day, 'steps', d.steps);
    set(d.day, 'activeKcal', d.active_calories);
  }
  for (const d of b.daily_spo2?.data ?? []) set(d.day, 'spo2Pct', d.spo2_percentage?.average);

  // Group sleep records by day, then take each day's main night.
  const byDay = new Map<string, OuraSleep[]>();
  for (const r of b.sleep?.data ?? []) {
    if (!r.day) continue;
    const list = byDay.get(r.day) ?? [];
    list.push(r);
    byDay.set(r.day, list);
  }
  for (const [day, records] of byDay) {
    const main = mainSleepRecord(records);
    if (!main) continue;
    set(day, 'totalSleepSec', main.total_sleep_duration);
    set(day, 'remSleepSec', main.rem_sleep_duration);
    set(day, 'deepSleepSec', main.deep_sleep_duration);
    set(day, 'hrvMs', main.average_hrv);
    set(day, 'restingHr', main.lowest_heart_rate ?? main.average_heart_rate);
  }
  return out;
}

/**
 * Import a line from the existing `oura-ledger.jsonl` produced on the Mac.
 * Lets years of history come across without re-querying the API.
 */
export function parseLedgerRow(row: unknown, at: string): { day: Day; fields: Record<string, Reading> } | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  const day = typeof r.day === 'string' ? r.day : null;
  if (!day) return null;
  const fields: Record<string, Reading> = {};
  const map: Record<string, string> = {
    sleep_score: 'sleepScore',
    readiness_score: 'readinessScore',
    activity_score: 'activityScore',
    hrv_avg: 'hrvMs',
    rhr_avg: 'restingHr',
    lowest_rhr: 'restingHr',
    temp_deviation: 'tempDeviationC',
    total_sleep_sec: 'totalSleepSec',
    rem_sleep_sec: 'remSleepSec',
    deep_sleep_sec: 'deepSleepSec',
    steps: 'steps',
    cal_active: 'activeKcal',
    spo2_avg: 'spo2Pct',
  };
  for (const [src, field] of Object.entries(map)) {
    const value = r[src];
    if (typeof value === 'number' && Number.isFinite(value) && fields[field] === undefined) {
      fields[field] = { value, source: 'oura', at };
    }
  }
  return Object.keys(fields).length ? { day, fields } : null;
}
